import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { ChevronLeft, ChevronRight, MessagesSquare, Send, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import { useSessionStore } from '@stores/useSessionStore'
import { useUIStore } from '@stores/useUIStore'
import { dateDifference } from '@/utils/date'
import { ExternalStepKey, HTTP_STATUS } from '@/constants'
import apiClient from '@/services/api'
const poweredByImg = '/assets/images/solvpath-logo.svg'
import { useChatInactivity } from '@/hooks/useChatInactivity'

interface ChatMessage {
  text: string
  isUser: boolean
  sentAt: Date | null
  time: string | null
  id?: string
  feedback?: number
}

interface ChatbotProps {
  onClose: () => void
}

const CHAT_BOT_URL = import.meta.env.VITE_CHAT_BOT_URL

function getTimeDiff(sentAt: Date): {
  diffInMinutes: number
  diffInHours: number
} {
  const ms = dateDifference(sentAt, new Date())
  const diffInMinutes = Math.floor(ms / 60000)
  const diffInHours = Math.floor(ms / 3600000)
  return { diffInMinutes, diffInHours }
}

const Chatbot = ({ onClose }: ChatbotProps) => {
  const session = useSessionStore(s => s.session)
  const campaign = useSessionStore(s => s.campaign)
  const token = useSessionStore(s => s.token)
  const isSessionUserKnown = useSessionStore(s => s.isSessionUserKnown())
  const setLoading = useUIStore(s => s.setLoading)
  const ready = useUIStore(s => s.ready)

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = localStorage.getItem('chat')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        /* ignore */
      }
    }
    return [
      {
        text: '<div><p>Hi there! How may I help you?</p></div>',
        sentAt: new Date(),
        isUser: false,
        time: null,
      },
    ]
  })
  const [currentMessage, setCurrentMessage] = useState('')
  const [processing, setProcessing] = useState(false)
  const [chatStarted, setChatStarted] = useState(false)
  const [botTyping, setBotTyping] = useState(false)

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const textInputRef = useRef<HTMLTextAreaElement>(null)
  const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Inactivity timer system
  useChatInactivity()

  // Dispatch chat-started event when chatStarted changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('chat-started', { detail: chatStarted }))
  }, [chatStarted])

  // Handle chat-ended event (close chatbot when inactivity sequence completes)
  useEffect(() => {
    const handler = () => onClose()
    window.addEventListener('chat-ended' as any, handler)
    return () => window.removeEventListener('chat-ended' as any, handler)
  }, [onClose])

  const campaignAny = campaign as any
  const hasChatIcon = campaignAny?.chatbot_icon
  const chatIconUrl: string | null = hasChatIcon ? campaignAny.chatbot_icon.file : null
  const quickLinks: any[] = campaignAny?.quick_links ?? []
  const accountId = campaignAny?.account_id

  const fetchParams = useMemo(
    () => ({
      method: 'POST' as const,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  )

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        const last = messagesContainerRef.current.lastElementChild
        if (last) {
          last.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }, 100)
  }, [])

  const calculateTime = useCallback(() => {
    setMessages(prev => {
      const updated = [...prev]
      for (let i = 0; i < updated.length; i++) {
        if (i !== updated.length - 1 && updated[i].isUser === updated[i + 1]?.isUser) {
          updated[i] = { ...updated[i], time: null, sentAt: null }
        }
        if (updated[i].sentAt) {
          const { diffInMinutes, diffInHours } = getTimeDiff(new Date(updated[i].sentAt!))
          const time =
            diffInHours > 0 ? `${diffInHours}h ago` : diffInMinutes === 0 ? 'Just now' : `${diffInMinutes}m ago`
          updated[i] = { ...updated[i], time }
        }
      }
      return updated
    })
  }, [])

  const toggleChatbot = useCallback(() => {
    setChatStarted(prev => {
      const next = !prev
      if (next) {
        scrollToBottom()
        setTimeout(() => calculateTime(), 50)
        timeIntervalRef.current = setInterval(calculateTime, 60000)
      } else {
        if (timeIntervalRef.current) {
          clearInterval(timeIntervalRef.current)
          timeIntervalRef.current = null
        }
        localStorage.removeItem('chat')
      }
      return next
    })
  }, [scrollToBottom, calculateTime])

  const goBack = useCallback(() => {
    setChatStarted(false)
    localStorage.removeItem('chat')
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current)
      timeIntervalRef.current = null
    }
  }, [])

  const openLink = useCallback(
    async (quickLink: any) => {
      if (!quickLink) {
        return
      }
      setLoading(true)
      try {
        await apiClient.post('/faq_chat/quick_link/', { quick_link: quickLink.id })
        if (quickLink.step_key === ExternalStepKey) {
          window.open(quickLink.link, '_blank')
        } else {
          window.location.href = quickLink.link
        }
      } catch {
        /* fall through to ready() */
      } finally {
        ready()
      }
    },
    [setLoading, ready]
  )

  const appendMessage = useCallback(
    async (index: number, newMessage: string) => {
      setMessages(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = {
            ...updated[index],
            text: updated[index].text + newMessage,
          }
        } else {
          updated.push({
            isUser: false,
            time: 'Just now',
            sentAt: new Date(),
            text: '<div><p>' + newMessage,
          })
        }
        return updated
      })
      await new Promise(r => setTimeout(r, 30))
      scrollToBottom()
    },
    [scrollToBottom]
  )

  const messageEnd = useCallback((index: number, id: string) => {
    setMessages(prev => {
      const updated = [...prev]
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          id: id.replace(/[^0-9]/g, ''),
          text: (updated[index].text + '</p></div>')
            .replaceAll('#$#', '')
            .replaceAll(/ MESSAGE_ID:[0-9]\d+ /gm, '')
            .replaceAll('{brand}.solvpath.com', window.location.host),
        }
      }
      return updated
    })
  }, [])

  const sendMessage = useCallback(async () => {
    if (processing || !currentMessage || currentMessage.trim() === '') {
      return
    }

    // Dispatch user-replied for inactivity tracking
    window.dispatchEvent(new CustomEvent('user-replied'))

    setProcessing(true)
    setBotTyping(true)

    const userMessage: ChatMessage = {
      isUser: true,
      time: 'Just now',
      sentAt: new Date(),
      text: currentMessage,
    }
    const messageText = currentMessage
    setCurrentMessage('')

    setMessages(prev => {
      const updated = [...prev, userMessage]
      return updated
    })
    scrollToBottom()

    const url = CHAT_BOT_URL + 'v1/chats'

    try {
      const response = await fetch(url, {
        ...fetchParams,
        body: JSON.stringify({
          parent_session_id: (session as any)?.parent_session_id || session?.id,
          session_id: session?.id,
          campaign_id: campaign?.id,
          account_id: accountId,
          question: messageText,
        }),
      })

      if (response.ok) {
        setProcessing(false)
        const reader = response.body!.getReader()
        let responseCompleted = false
        let enableLinkText = false
        let enableLinkUrl = false
        let linkUrl = ''
        let linkText = ''
        let messageId = ''
        let msgIndex = -1

        // Get the current index after state update
        setMessages(prev => {
          msgIndex = prev.length
          return prev
        })

        while (true) {
          const { done, value } = await reader.read()

          if (value && value.length > 0) {
            setProcessing(false)
            for (let i = 0; i < value.length; i++) {
              if (!responseCompleted) {
                let newMessage = ''
                if (value[i] === 10 && value[i - 1] && value[i - 1] !== 10) {
                  newMessage += '<br>'
                } else {
                  if (value[i] === 91 || value[i] === 93) {
                    enableLinkText = value[i] === 91
                    continue
                  } else if (value[i] === 40 || value[i] === 41) {
                    enableLinkUrl = value[i] === 40
                    continue
                  }

                  if (enableLinkUrl || enableLinkText) {
                    if (enableLinkText) {
                      linkText += String.fromCharCode(value[i])
                    }
                    if (enableLinkUrl) {
                      linkUrl += String.fromCharCode(value[i])
                    }
                    continue
                  } else if (linkText && linkUrl) {
                    newMessage += `<a target="_blank" href="${linkUrl}">${linkText}</a>`
                    newMessage += value[i] === 10 ? '<br><br>' : String.fromCharCode(value[i])
                    linkUrl = ''
                    linkText = ''
                    enableLinkText = enableLinkUrl = false
                    newMessage += '<br>'
                  } else {
                    if (value[i] === 35 && value[i + 1] === 36 && value[i + 2] === 35) {
                      responseCompleted = true
                    } else if (
                      value[i] === 226 &&
                      value[i + 1] === 128 &&
                      (value[i + 2] === 156 || value[i + 2] === 157)
                    ) {
                      newMessage += '"'
                      i = i + 2
                    } else {
                      newMessage += String.fromCharCode(value[i])
                    }
                  }
                }
                newMessage = newMessage.replaceAll('{brand}.solvpath.com', window.location.host)
                if (newMessage) {
                  await appendMessage(msgIndex, newMessage)
                }
              } else {
                messageId += String.fromCharCode(value[i])
              }
            }
          }

          if (done || responseCompleted) {
            messageEnd(msgIndex, messageId)
            window.dispatchEvent(new CustomEvent('chatbot-replied'))
            break
          }
        }
      } else {
        if (response.status === HTTP_STATUS.UNAUTHORIZED) {
          onClose()
        } else {
          setMessages(prev => [
            ...prev,
            {
              isUser: false,
              time: 'Just now',
              sentAt: new Date(),
              text: 'Sorry, we are unable to understand your query. Please try again',
            },
          ])
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      textInputRef.current?.focus()
      setProcessing(false)
      setBotTyping(false)
      setMessages(prev => {
        localStorage.setItem('chat', JSON.stringify(prev))
        return prev
      })
    }
  }, [
    processing,
    currentMessage,
    session,
    campaign,
    accountId,
    fetchParams,
    scrollToBottom,
    appendMessage,
    messageEnd,
    onClose,
  ])

  const sendMessageFeedback = useCallback(
    async (message: ChatMessage, rating: number) => {
      if (message.feedback === rating) {
        return
      }

      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === message.id)
        if (idx === -1) {
          return prev
        }
        const updated = [...prev]
        updated[idx] = { ...updated[idx], feedback: rating }
        return updated
      })

      const url = CHAT_BOT_URL + 'v1/feedback'
      try {
        await fetch(url, {
          ...fetchParams,
          body: JSON.stringify({
            session_id: session?.id,
            message_id: +message.id!,
            rating: rating === 0 ? 'thumbs-down' : 'thumbs-up',
            feedback: '',
          }),
        })
        setMessages(prev => {
          localStorage.setItem('chat', JSON.stringify(prev))
          return prev
        })
      } catch (err) {
        console.error(err)
      }
    },
    [session, fetchParams]
  )

  const sendInactivityMessage = useCallback(
    (text: string[]) => {
      setMessages(prev => {
        const index = prev.length
        let msgs = [...prev]
        for (let i = 0; i < text.length; i++) {
          if (msgs[index]) {
            msgs[index] = { ...msgs[index], text: msgs[index].text + text[i] }
          } else {
            msgs.push({
              isUser: false,
              time: 'Just now',
              sentAt: new Date(),
              text: '<div><p>' + text[i],
            })
          }
        }
        // Close the div
        if (msgs[index]) {
          msgs[index] = {
            ...msgs[index],
            text: msgs[index].text + '</p></div>',
          }
        }
        localStorage.setItem('chat', JSON.stringify(msgs))
        return msgs
      })
      calculateTime()
    },
    [calculateTime]
  )

  useEffect(() => {
    const handler = (e: CustomEvent) => sendInactivityMessage(e.detail)
    window.addEventListener('chatbot-inactivity-message' as any, handler)
    return () => window.removeEventListener('chatbot-inactivity-message' as any, handler)
  }, [sendInactivityMessage])

  useEffect(() => {
    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current)
      }
    }
  }, [])

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className={`chatbot-widget${chatStarted ? ' chat-started' : ''}`}>
      {/* Header */}
      <div className={`chatbot-header${chatStarted ? ' chatbot-fixed' : ''}`}>
        <div className={`chatbot-header-left${chatStarted ? ' justify-center' : ''}`}>
          {!chatStarted ? (
            <div className="chatbot-header-icon">
              {chatIconUrl ? (
                <img src={chatIconUrl} alt={`${campaignAny?.display_name} Logo`} />
              ) : (
                <MessagesSquare size={26} strokeWidth={1.8} style={{ color: 'var(--theme-color)' }} />
              )}
            </div>
          ) : (
            <div className="header-icon" onClick={goBack}>
              <ChevronLeft size={20} color="white" strokeWidth={2} />
            </div>
          )}
          {chatStarted && <span>{campaignAny?.display_name} Support</span>}
          <div className="header-icon close-icon flex sm:hidden" onClick={onClose}>
            <X size={18} color="white" strokeWidth={2.5} />
          </div>
        </div>
      </div>

      {!chatStarted ? (
        <>
          {/* Sub-header */}
          <div className="chatbot-sub-header">
            <div className="chatbot-sub-header-title">
              Hello
              {isSessionUserKnown && (session as any)?.customer?.first_name
                ? ` ${(session as any).customer.first_name}`
                : ''}
              !
            </div>
            <div className="chatbot-sub-header-description">
              Thanks for giving us the opportunity to help you. Please make your selection below.
            </div>
          </div>

          {/* Content */}
          <div className="chatbot-content">
            {/* Chat with us */}
            <div className="chatbot-quicklinks">
              <div className="chatbot-quicklink" onClick={toggleChatbot}>
                <div className="chatbot-quicklink-left">
                  <div className="chatbot-quicklink-title bottom-title">Chat with us</div>
                  <div className="chatbot-quicklink-subtitle">We typically reply within a minute</div>
                </div>
                <div className="chatbot-quicklink-right">
                  <Send size={20} className="message-icon" style={{ color: 'var(--theme-color)' }} />
                </div>
              </div>
            </div>

            {/* Quick links */}
            {quickLinks && quickLinks.length > 0 && (
              <div className="chatbot-quicklinks">
                <div className="chatbot-quicklink heading">
                  <div className="chatbot-quicklink-left">
                    <div className="chatbot-quicklink-title top-title">Quick Links</div>
                  </div>
                </div>
                {(quickLinks as any[]).map((quickLink: any) => (
                  <React.Fragment key={`quicklink-${quickLink.id}`}>
                    <div className="chatbot-quicklink" onClick={() => openLink(quickLink)}>
                      <div className="chatbot-quicklink-left">
                        <div className="chatbot-quicklink-title">{quickLink.title}</div>
                        {quickLink.subtitle && <div className="chatbot-quicklink-subtitle">{quickLink.subtitle}</div>}
                      </div>
                      <div className="chatbot-quicklink-right">
                        <ChevronRight size={14} color="#181818" strokeWidth={1.5} />
                      </div>
                    </div>
                    <hr />
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <div className="powered-by-solvpath">
            <span>Powered by</span>
            <img src={poweredByImg} alt="SolvPath" />
          </div>
        </>
      ) : (
        <>
          {/* Messages body */}
          <div className="chatbot-body" ref={messagesContainerRef}>
            {messages.map((message, index) => (
              <div key={index} className={`chat-message${message.isUser ? ' user-message' : ''}`}>
                {!message.isUser && (
                  <div className="logo">
                    {chatIconUrl ? (
                      <img src={chatIconUrl} alt="" />
                    ) : (
                      <MessagesSquare size={16} strokeWidth={1.8} style={{ color: 'var(--theme-color)' }} />
                    )}
                  </div>
                )}
                <div className={`flex flex-col${!message.isUser && message.id ? ' mb-3' : ''}`}>
                  <div className="message-text">
                    <span dangerouslySetInnerHTML={{ __html: message.text }} />
                    {!message.isUser && message.id && index >= 3 && (
                      <div className="feedback">
                        <ThumbsUp
                          size={17}
                          color="white"
                          fill="white"
                          strokeWidth={1.5}
                          onClick={() => sendMessageFeedback(message, 1)}
                          className={`cursor-pointer ${message.feedback === 1 ? 'has-feedback' : ''}`}
                        />
                        <ThumbsDown
                          size={17}
                          color="white"
                          fill="white"
                          strokeWidth={1.5}
                          onClick={() => sendMessageFeedback(message, 0)}
                          className={`cursor-pointer ${message.feedback === 0 ? 'has-feedback' : ''}`}
                        />
                      </div>
                    )}
                  </div>
                  {message.time && <span className="message-time">{message.time}</span>}
                </div>
              </div>
            ))}

            {/* Loader dots */}
            {processing && (
              <div className="chat-message loader-message">
                <div className="logo">
                  {chatIconUrl ? (
                    <img src={chatIconUrl} alt="" />
                  ) : (
                    <MessagesSquare size={20} strokeWidth={1.8} style={{ color: 'var(--theme-color)' }} />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="message-text typing-dots">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="chatbot-fixed chatbot-footer">
            <div>
              <textarea
                ref={textInputRef}
                value={currentMessage}
                onChange={e => setCurrentMessage(e.target.value)}
                className="form-control message-input"
                placeholder={botTyping ? '' : 'Start typing...'}
                onKeyPress={handleKeyPress}
                disabled={botTyping}
              />
              <div className="send-icon" onClick={sendMessage}>
                <Send size={20} style={{ color: 'var(--theme-color)' }} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Chatbot
